const imported_stylus_components = require('.cache/react-style-loader/src/components/MonacoEditor.styl');
import React, { type ReactNode } from 'react';
import textBlock from '@pi/text-block';
import Editor from '@monaco-editor/react';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { type editor, type IDisposable, type languages, type Position } from 'monaco-editor';

import { UiContext } from './UiContext';

import type { EditorProps, Monaco, OnChange, OnMount } from '@monaco-editor/react';
import type { Field } from './Form';

const DEFAULT_OPTIONS: Partial<editor.IEditorOptions> = {
    scrollBeyondLastLine: false,
    // wordWrap: 'on',
    minimap: { enabled: false },
    suggest: {
        showKeywords: false,
        showModules: false,
        showVariables: false,
    },
    // quickSuggestions: {
    //     other: false,
    // },
};

export type MonacoEditorProps = EditorProps & {
    onBlur?: (value: string) => void;
    onKeyDown?: (event: KeyboardEvent, value: string | undefined) => void;
    style?: React.CSSProperties;
    minHeight?: number;
    maxHeight?: number;
    /**
     * A list of variables that are available in the editor for autocomplete suggestions
     */
    suggestionVariables?: string | Record<string, MonacoSuggestionVariable>;
    /**
     * A JSON schema to be used for validation and autocomplete in the editor
     */
    schema?: Record<string, any> | null | undefined;
    /**
     * A function that returns a list of suggestions for the editor
     */
    onSuggest?: (
        monaco: Monaco,
        model: editor.ITextModel,
        position: Position,
    ) => languages.CompletionItem[] | Promise<languages.CompletionItem[]>;
    highlights?: Array<MonacoHighlight>;
};

export interface MonacoHighlight {
    style: 'green' | 'red' | 'blue';
    start: number;
    end: number;
    margin?: boolean;
    line?: boolean;
}

export interface MonacoSuggestionVariable {
    comment?: string;
    value: NonNullable<any>;
}

export class MonacoEditor extends React.PureComponent<MonacoEditorProps> {
    static contextType = UiContext;
    context!: React.ContextType<typeof UiContext>;
    editor!: editor.IStandaloneCodeEditor;
    monaco!: any;
    lastChangeValue?: string;
    cleanupSuggestions?: () => any;
    lastDecorators: any[] = [];
    isUnmounted = false;
    _onBlurDisposable?: IDisposable;
    _onKeyDownDisposable?: IDisposable;
    _jsonSchemaDisposable?: IDisposable;
    _suggestDisposable?: IDisposable;

    static defaultProps = {
        minHeight: 48,
        maxHeight: 480,
    };

    state = {
        height: this.props.minHeight || 48,
    };

    componentDidUpdate(prevProps: MonacoEditorProps): void {
        if (this.isUnmounted) return;
        const { highlights, schema, onSuggest } = this.props;
        const { editor, monaco } = this;

        if (!editor || !monaco) return;

        if (schema !== prevProps.schema) {
            this.updateJsonSchema(monaco, editor, schema);
        }

        if (onSuggest !== prevProps.onSuggest) {
            this.updateSuggestions(monaco, editor, onSuggest);
        }

        if (!highlights?.length) {
            if (this.lastDecorators?.length) {
                this.lastDecorators = editor.deltaDecorations(this.lastDecorators, []);
            }
            return;
        }

        const newDecorators = highlights.map(({ start, end, style, margin, line }) => ({
            range: new monaco.Range(start, 1, end, 1),
            options: {
                isWholeLine: line,
                className: line ? 'monaco-decorator-' + style : undefined,
                marginClassName: margin ? 'monaco-decorator-' + style : undefined,
            },
        }));

        this.lastDecorators = editor.deltaDecorations(this.lastDecorators, newDecorators);
    }

    handleChange: OnChange = (value, ev) => {
        if (this.isUnmounted) return;
        this.props.onChange?.(value, ev);
    };

    handleMount: OnMount = (editor, monaco) => {
        const {
            onMount,
            onKeyDown,
            onBlur,
            height,
            minHeight,
            maxHeight,
            suggestionVariables,
            schema,
            onSuggest,
        } = this.props;

        this.editor = editor;
        this.monaco = monaco;

        onMount?.(editor, monaco);

        // enable auto-resize if a fixed height is not provided
        if (!height && minHeight && maxHeight) {
            const updateHeight = () => {
                const contentHeight = _.clamp(editor.getContentHeight(), minHeight, maxHeight);
                const container = editor.getContainerDomNode();
                editor.layout({ height: contentHeight, width: container.offsetWidth });
                this.setState({ height });
            };
            editor.onDidContentSizeChange(updateHeight);
            updateHeight();
        } else this.setState({ height: this.props.height });

        this.updateEvents(this.editor, onKeyDown, onBlur);

        addMonacoSuggestions(monaco);

        if (schema) {
            this.updateJsonSchema(monaco, editor, schema);
        }

        if (onSuggest) {
            this.updateSuggestions(monaco, editor, onSuggest);
        }

        if (suggestionVariables) {
            const serialize = (value: any) =>
                JSON.stringify(value, (_k, v) => (typeof v === 'function' ? v.toString() : v));

            const content =
                typeof suggestionVariables === 'string'
                    ? suggestionVariables
                    : _.map(
                          suggestionVariables,
                          ({ comment, value }, name) =>
                              textBlock`
                                /** ${comment} */
                                const ${name} = ${serialize(value)};
                            `,
                      ).join('\n');
            const cleanJS = monaco.languages.typescript.javascriptDefaults.addExtraLib(content);
            // const cleanTS = monaco.languages.typescript.typescriptDefaults.addExtraLib(content);
            this.cleanupSuggestions = () => {
                cleanJS.dispose();
                // cleanTS.dispose();
            };
        }
    };

    componentWillUnmount(): void {
        this.cleanupSuggestions?.();
        this._jsonSchemaDisposable?.dispose();
        this._suggestDisposable?.dispose();
        this.isUnmounted = true;
    }

    updateSuggestions(
        monaco: Monaco,
        editor: editor.IStandaloneCodeEditor,
        onSuggest: MonacoEditorProps['onSuggest'],
    ): void {
        this._suggestDisposable?.dispose();

        if (!onSuggest) return;

        const language = this.props.language || 'json';

        this._suggestDisposable = monaco.languages.registerCompletionItemProvider(language, {
            provideCompletionItems: async (model, position) => {
                // only provide suggestions for the current model
                if (model.uri.toString() !== editor.getModel()?.uri.toString()) {
                    return { suggestions: [] };
                }

                const suggestions = await onSuggest(monaco, model, position);
                return { suggestions };
            },
        });
    }

    updateJsonSchema(monaco: Monaco, editor: editor.IStandaloneCodeEditor, jsonSchema: any): void {
        this._jsonSchemaDisposable?.dispose();

        if (!jsonSchema) return;

        const model = editor.getModel();
        if (!model) return;

        const uri = model.uri.toString();

        const currentOptions = monaco.languages.json.jsonDefaults.diagnosticsOptions;
        const schemas = currentOptions.schemas || [];

        // filter out existing schemas for this URI
        const newSchemas = schemas.filter((s: any) => s.uri !== uri);

        newSchemas.push({
            uri: 'http://pinto-ui/' + Math.random().toString(36).substring(7),
            fileMatch: [uri],
            schema: jsonSchema,
        });

        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
            ...currentOptions,
            validate: true,
            schemas: newSchemas,
        });

        this._jsonSchemaDisposable = {
            dispose: () => {
                const options = monaco.languages.json.jsonDefaults.diagnosticsOptions;
                const schemas = options.schemas || [];
                monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                    ...options,
                    schemas: schemas.filter((s: any) => s.fileMatch?.[0] !== uri),
                });
            },
        };
    }

    // _onBlurDisposable?: IDisposable;
    // _onKeyDownDisposable?: IDisposable;

    updateEvents = memoizeOne(
        (editor: editor.IStandaloneCodeEditor, onKeyDown: any, onBlur: any) => {
            this._onBlurDisposable?.dispose();
            this._onKeyDownDisposable?.dispose();

            this._onKeyDownDisposable = editor?.onKeyDown((e: { browserEvent: KeyboardEvent }) => {
                const event = e.browserEvent;

                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                    onBlur?.(editor.getValue());
                    return;
                }

                onKeyDown?.(event, editor.getValue());
            });

            if (onBlur) {
                this._onBlurDisposable = editor?.onDidBlurEditorText(() =>
                    onBlur(editor.getValue()),
                );
            }
        },
    );

    render(): ReactNode {
        const { className, style, options, onBlur, onKeyDown, ...props } = this.props;
        const { height } = this.state;
        const { darkMode } = this.context;

        // why is this needed: https://github.com/suren-atoyan/monaco-react/issues/114
        if ('value' in props) {
            if (props.value == null) props.value = '';
            else props.value = String(props.value);
        }

        this.updateEvents(this.editor, onKeyDown, onBlur);

        if (this.isUnmounted) return null;

        return (
            <Root className={className} style={{ ...style, height }}>
                <Editor
                    defaultLanguage='json'
                    options={options ? { ...DEFAULT_OPTIONS, ...options } : DEFAULT_OPTIONS}
                    height={height /* must be before ...props */}
                    theme={darkMode ? 'vs-dark' : undefined}
                    {...props}
                    onChange={this.handleChange}
                    onMount={this.handleMount}
                />
            </Root>
        );
    }
}

const addMonacoSuggestions = _.once((monaco: Monaco) => {
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        noLib: true,
        allowNonTsExtensions: true,
    });
});

export interface MonacoEditorFieldProps
    extends Omit<MonacoEditorProps, 'value' | 'defaultValue' | 'onChange' | 'onBlur'> {
    field: Field<string>;
    changeOnBlur?: boolean;
}

export const MonacoEditorField: React.FC<MonacoEditorFieldProps> = ({
    field,
    changeOnBlur,
    ...props
}) => (
    <MonacoEditor
        minHeight={160}
        maxHeight={500}
        {...props}
        value={field.value == null ? '' : field.value}
        defaultValue={field.value == null ? '' : field.value}
        onChange={changeOnBlur ? undefined : field.onChange}
        onBlur={changeOnBlur ? field.onChange : undefined}
    />
);

/**
 * Assumes the JSON is encoded with JSON.stringify(json, null, 4)
 */
export function getJsonHighlightStart(json: Record<string, any>, pathStr: string): number | null {
    const path = _.toPath(pathStr);
    if (!path.length) return null;

    json = JSON.parse(JSON.stringify(json));
    const tag = '>🥞>TAG<🥞<';
    _.set(json, path, tag);
    const index = JSON.stringify(json, null, 4)
        .split('\n')
        .findIndex(x => x.includes(tag));

    if (index === -1) return null;

    return index + 1;
}

const Root = imported_stylus_components.Root;